import {
  chargeDateNeedsDay,
  isDayGranularDate,
  startDateNeedsDay,
} from "../domain/granularity.ts";
import { monthOf } from "../domain/months.ts";
import { occurrencesOf } from "../domain/occurrences.ts";
import type {
  Anchoring,
  Dataset,
  IsoDate,
  Purchase,
  Recurrence,
  RecurringCost,
} from "../domain/types.ts";
import type { PurchaseDraft } from "./purchaseForm.ts";

/**
 * Whether `date` parses as a month at all. `monthOf` stays strict (it is a
 * domain function used across the app, and AGENTS.md forbids softening a
 * domain throw into a fallback) — this wraps it rather than loosening it, so
 * the UI layer can tell "not a date yet" apart from "a bug" without touching
 * `src/domain/`.
 *
 * A purchase's date starts as a whole month and can be widened to a real day,
 * but the native `<input type="date">` lets the owner clear it outright —
 * that is a half-finished FORM, not a domain value, and belongs handled here.
 */
function isParseableMonth(date: IsoDate): boolean {
  try {
    monthOf(date);
    return true;
  } catch {
    return false;
  }
}

/**
 * The costs a purchase dated `date` could be a charge for.
 *
 * An anchored (`lastCharge`) cost has no grid of slots, so any DAY can be the
 * moment its cycle restarted — that IS the refresh. It needs a day, though:
 * the purchase's own date becomes the anchor the series steps forward from
 * (`chargeDateNeedsDay`), so a month-only date makes the tag unsaveable, and
 * both write boundaries refuse it. The owner must not be offered a choice that
 * then fails — the same reason `newCostOptionIsSelectable` disables the
 * new-cost pairings a month-only date cannot express.
 *
 * A `calendar` cost is offerable only where its grid actually has a slot in
 * that purchase's month; tagging one outside its grid would record a charge
 * settling nothing. Its charge's own date carries no granularity rule at all —
 * the slot it settles lives in `source.occurrenceDate`.
 *
 * `date` can be empty: the purchase dialog calls this unconditionally on
 * every render, and the native `<input type="date">` lets the owner clear it.
 * An empty (or otherwise unparseable) date is not an error to report here —
 * it is just a form the owner has not finished — so nothing is offered rather
 * than throwing `monthOf`'s parse error out of a render body.
 */
export function offerableCosts(dataset: Dataset, date: IsoDate): RecurringCost[] {
  if (!isParseableMonth(date)) return [];
  const month = monthOf(date);
  return dataset.recurring
    .filter((cost) => !cost.archived)
    .filter((cost) => {
      if (chargeDateNeedsDay(cost.anchoring)) return isDayGranularDate(date);
      return occurrencesOf(cost, dataset.purchases, month).some(
        (occurrence) => monthOf(occurrence.date) === month,
      );
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * The `source` a purchase gets when tagged to a cost.
 *
 * An anchored cost has no slots, so the purchase's own date is the whole
 * story — the charge itself is what rebases the series.
 *
 * A calendar cost's charge settles a specific slot, and that slot is NOT
 * always `{ recurringId, occurrenceDate: monthOf(purchaseDate) }`: that
 * shape is only correct for a month-granular grid (`everyNMonths` under
 * `calendar`). `everyNDays` + `calendar` is a legal combination too, and its
 * slots are specific DAYS — deriving the slot from the month alone would
 * name a slot that does not exist on that grid.
 *
 * So the slot is resolved by LOOKUP instead of derivation: walk the cost's
 * occurrences through the purchase's month and take whichever one actually
 * falls there. That is correct for both a month-granular grid and a
 * day-granular calendar cost. If nothing falls in that month, the cost
 * should never have been offered — throw rather than invent a slot.
 *
 * Unlike `offerableCosts`, this does NOT guard against an empty
 * `purchaseDate` by returning something inert — it is only ever called at
 * save time (inside `store.mutate`, via `useMutate`), never from a render
 * body, so a bad date here surfaces as a caught save-time error, exactly like
 * every other bad-date save already does (`ensureMonth`'s `monthOf` in
 * `addPurchase`). Softening it would invent a second rule for the same
 * situation rather than reuse the one that already exists.
 */
export function sourceFor(
  dataset: Dataset,
  cost: RecurringCost,
  purchaseDate: IsoDate,
): Purchase["source"] {
  if (cost.anchoring === "lastCharge") return { recurringId: cost.id };

  const month = monthOf(purchaseDate);
  const occurrence = occurrencesOf(cost, dataset.purchases, month).find(
    (candidate) => monthOf(candidate.date) === month,
  );
  if (!occurrence) {
    throw new Error(
      `"${cost.name}" has no occurrence in ${month}, so a purchase dated ${purchaseDate} cannot ` +
        `be a charge for it.`,
    );
  }
  return { recurringId: cost.id, occurrenceDate: occurrence.date };
}

/**
 * Whether a NEW cost's kind+anchoring pairing can be seeded from a purchase
 * dated `purchaseDate`.
 *
 * `seedCostFromDraft` hands `purchaseDate` straight to `startDate` whenever
 * the pairing is not month-granular, so this asks the domain's own
 * `startDateNeedsDay` rather than restating it: every pairing needs a real day
 * EXCEPT "everyNMonths" anchored to "calendar" — rent occurs IN a month, so a
 * month-only date is fine there.
 *
 * A month-only `purchaseDate` reaching any OTHER pairing would reach the
 * store's own validation and throw text naming a field ("startDate") the
 * purchase dialog never shows to begin with — so the control offering these
 * choices must not offer one that always fails; it disables it instead
 * (PurchaseDialog's "+ New recurring cost…" unit and anchoring selects).
 * Exported so each option can check this without duplicating the rule, and so
 * it is testable on its own — a DOM test cannot exercise a predicate.
 *
 * An empty `purchaseDate` (the native date input, cleared) fails EVERY
 * pairing, including "everyNMonths" + "calendar": that combination is
 * otherwise always fine, but it still hands `purchaseDate` to `monthOf` in
 * `seedCostFromDraft`, and an empty string does not even parse as a month.
 */
export function newCostOptionIsSelectable(
  kind: Recurrence["kind"],
  anchoring: Anchoring,
  purchaseDate: IsoDate,
): boolean {
  if (!isParseableMonth(purchaseDate)) return false;
  return !startDateNeedsDay(kind, anchoring) || isDayGranularDate(purchaseDate);
}

/**
 * Like `sourceFor`, this does NOT guard against an empty `draft.date` — it is
 * only ever called at save time, inside `store.mutate`. The dialog's
 * "+ New recurring cost…" options are already disabled for an empty date via
 * `newCostOptionIsSelectable`, so reaching this with one would mean the
 * combo was selected before the date was cleared; the resulting throw (from
 * `monthOf` here, or from the store's own `requireStartDateGranularity`) is
 * caught the same way any other bad-date save already is.
 */
export function seedCostFromDraft(
  draft: PurchaseDraft,
  recurrence: Recurrence,
  anchoring: Anchoring,
): Omit<RecurringCost, "id" | "order" | "parentPostId"> {
  const monthGranular = recurrence.kind === "everyNMonths" && anchoring === "calendar";
  return {
    name: draft.description,
    // Unfiled; the dialog nests a single-post bill under its post afterwards.
    folderId: null,
    archived: false,
    amount: { amount: draft.amount, currency: draft.currency },
    startDate: monthGranular ? monthOf(draft.date) : draft.date,
    recurrence,
    anchoring,
    splitMode: draft.splitMode,
    splits: draft.splits.map((s) => ({ ...s })),
  };
}
